<template>
  <div
    v-observe-visibility="blockVisibility.getOptions('lazyLoad')"
    :class="[$style.block, canRenderBlock ? $style.visible : '']"
  >
    <template v-if="hasPersonalRecommendedBlock">
      <div v-observe-visibility="blockVisibility.getOptions('all')" :class="$style.title">
        Читайте также
      </div>
      <div
        v-observe-visibility="blockVisibility.getOptions('half')"
        :class="$style.personalRecommendedBlock"
      >
        <personal-selection-place
          v-for="(record, index) of personalsRecords.part1"
          :key="record.id"
          :record="record"
          :place-number="index + 1"
          @recordShown="recordShowHandler(record, index + 1)"
          @recordClicked="recordClickHandler(record, index + 1)"
        >
          <AdvCreative
            v-if="index === 0"
            slot="adv"
            :adv="advBlock1"
            :clientRender="true"
            :class="$style.advBlock"
          />
          <AdvCreative
            v-else
            slot="adv"
            :adv="advBlock2"
            :clientRender="true"
            :class="$style.advBlock"
          />
        </personal-selection-place>

        <div v-if="!isMobileOnly" :class="$style.banner">
          <AdvCreative :key="`adv-middle`" :adv="middleAdvBlock" :clientRender="true" />
        </div>

        <personal-selection-place
          v-for="(record, index) of personalsRecords.part2"
          :key="record.id"
          :record="record"
          :place-number="index + personalsRecords.part1.length + 1"
          @recordShown="
            recordShowHandler(record, index + personalsRecords.part1.length + 1)
          "
          @recordClicked="
            recordClickHandler(record, index + personalsRecords.part1.length + 1)
          "
        >
          <AdvCreative
            slot="adv"
            :adv="advBlock3"
            :clientRender="true"
            :class="$style.advBlock"
          />
        </personal-selection-place>
      </div>
    </template>
    <blocked-user-modal
      v-if="isBlockedUserModalRendered"
      v-model="isBlockedUserModalOpened"
    />
  </div>
</template>

<script>
import { guardEmptyArray, guardUnspecified } from '@portal/utils/util-guards';
import { ObserverVisibility } from '../../../../shared';

import { StoreMixin } from '../../../../shared/mixins';
import { RecordLastNews } from '../../components/record-last-news';
import PersonalSelectionPlace from './personal-selection-place/personal-selection-place.component.vue';
import { PersonalSelectionUseCase } from './personal-selection.usecase';
import { AdvCreative } from '../../../../shared/components/_adv';

// eslint-disable-next-line @typescript-eslint/naming-convention
const BlockedUserModal = () =>
  import(/* webpackChunkName: "blocked-user-modal" */ '@jtnews/layout/modals').then(
    m => m.BlockedUserModal
  );

export default {
  name: 'RecordPersonalSelectionABC',
  components: {
    RecordLastNews,
    PersonalSelectionPlace,
    BlockedUserModal,
    AdvCreative
  },
  mixins: [StoreMixin],
  data() {
    return {
      useCase: null,
      blockVisibility: new ObserverVisibility(['lazyLoad', 'half', 'all']),
      canRenderBlock: false,
      personalsRecords: null,
      reachGoals: { general: [], view: '' },
      isBlockedUserModalOpened: false,
      isBlockedUserModalRendered: false
    };
  },
  computed: {
    regionId() {
      return this.store.regionId;
    },
    envType() {
      return this.store.envType;
    },
    isBlockedUser() {
      const userInfo = this.store.commonModule.userInfo;
      return guardUnspecified(userInfo) && userInfo.isBlocked;
    },
    hasPersonalRecommendedBlock() {
      return guardUnspecified(this.personalsRecords);
    },
    isMobile() {
      return this.store.deviceInfo.isMobile;
    },
    isMobileOnly() {
      return this.isMobile && !this.store.deviceInfo.isTablet;
    },
    middleAdvBlock() {
      return this.store.advModule.personalSelectionMiddleDesktopAdvBlock;
    },
    advBlock1() {
      return this.isMobile
        ? this.store.advModule.personalSelectionMobile1AdvBlock
        : this.store.advModule.personalSelectionDesktop1AdvBlock;
    },
    advBlock2() {
      return this.isMobile
        ? this.store.advModule.personalSelectionMobile2AdvBlock
        : this.store.advModule.personalSelectionDesktop2AdvBlock;
    },
    advBlock3() {
      return this.isMobile
        ? this.store.advModule.personalSelectionMobile3AdvBlock
        : this.store.advModule.personalSelectionDesktop3AdvBlock;
    }
  },
  beforeMount() {
    this.useCase = new PersonalSelectionUseCase(this.envType);
  },
  mounted() {
    this.blockVisibility.event.subscribe(async state => {
      if (state.hook === 'lazyLoad' && state.value && !this.canRenderBlock) {
        await this.getSavedBlockData();

        if (!guardUnspecified(this.personalsRecords)) {
          this.getBlockData();
        } else {
          this.canRenderBlock = true;
        }
      }

      if (state.hook === 'half' && state.value && !this.isMobileOnly) {
        this.sendReachGoal(this.reachGoals.view);
        this.blockShowHandler();
      }

      if (state.hook === 'all' && state.value && this.isMobileOnly) {
        this.sendReachGoal(this.reachGoals.view);
        this.blockShowHandler();
      }
    });
  },
  beforeDestroy() {
    this.blockVisibility.destroy();
  },
  methods: {
    async getSavedBlockData() {
      const block = await this.useCase.getSavedBlockData();

      if (guardUnspecified(block)) {
        this.personalsRecords = block.data;
        this.reachGoals = { ...block.reachGoal };
      }
    },
    async getBlockData() {
      try {
        const result = await this.useCase.getBlockData(this.regionId, this.isMobileOnly);

        if (guardUnspecified(result)) {
          const { data, reachGoal } = result;

          this.personalsRecords = data;
          this.reachGoals = { ...reachGoal };
        }

        this.sendReachGoal(this.reachGoals.general);
      } catch (err) {
        this.personalsRecords = null;
      } finally {
        this.canRenderBlock = true;
      }
    },
    blockShowHandler() {
      this.sendReachGoalParams({
        goalName: 'viewRelapBlock',
        actionType: 'Просмотр блок'
      });
    },
    recordShowHandler(record, placeNumber) {
      this.sendReachGoalParams({
        goalName: 'viewRelap',
        actionType: 'Просмотр контент',
        prop1: record.url,
        prop2: {
          [placeNumber]: record.type
        }
      });
    },
    recordClickHandler(record, placeNumber) {
      this.sendReachGoalParams({
        goalName: 'clickRelap',
        actionType: 'Клик',
        prop1: record.url,
        prop2: {
          [placeNumber]: record.type
        }
      });
    },
    openBlockedUserModal() {
      this.isBlockedUserModalOpened = true;
      this.isBlockedUserModalRendered = true;
    },
    sendReachGoal(names) {
      if (guardEmptyArray(names)) {
        names.forEach(name => {
          this.store.analyticsModule.sendReachGoal({ name });
        });
      }
    },
    sendReachGoalParams(data) {
      const { actionType, goalName, prop1, prop2 } = data;
      const blockType = 'Центральная колонка';
      const fieldType = 'Релап (блок)';

      let valueName;

      if (guardUnspecified(prop1) && guardUnspecified(prop2)) {
        valueName = {
          [actionType]: {
            [prop1]: prop2
          }
        };
      } else {
        valueName = actionType;
      }

      this.store.analyticsModule.sendNewReachGoal({
        blockType,
        fieldType,
        valueName,
        goalName,
        willDeprecate: true
      });
      this.store.analyticsModule.sendNewFormatReachGoal({
        blockType: fieldType,
        actionType,
        goalName,
        prop1,
        prop2
      });
    }
  }
};
</script>

<style lang="scss" module>
@import 'personal-selection.styles';
</style>
